/**
 * useConnectionManager Hook
 * 
 * Handles WebSocket connection lifecycle, retry logic, and error handling.
 * Extracted from useWebSocketSession for better separation of concerns.
 * 
 * XSTATE MIGRATION: Now dispatches events to state machine instead of direct setState.
 */

import { useCallback, useRef } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { WebAudioManager } from '../../services/audio';
import { defaultCreateHeaders } from '../../types/auth';
import { logger } from '../../utils/logger';
import {
  DEFAULT_MAX_RETRY_ATTEMPTS,
  DEFAULT_RETRY_BASE_DELAY_MS,
  MAX_RETRY_DELAY_MS,
  RETRY_JITTER_MAX_MS,
} from './constants';
import type { WebSocketSessionConfig, SendFunction, ConnectOptions } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseConnectionManagerParams {
  config: WebSocketSessionConfig;
  send: SendFunction;
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  audioManagerRef: React.MutableRefObject<WebAudioManager | null>;
  systemInstructionsRef: React.MutableRefObject<string>;
  pendingSessionConfigRef: React.MutableRefObject<any>;
  isRecordingForBlobRef: React.MutableRefObject<boolean>;
  recordedAudioChunksRef: React.MutableRefObject<Uint8Array[]>;
}

export interface UseConnectionManagerReturn {
  connect: (options?: ConnectOptions) => Promise<void>;
  disconnect: () => Promise<void>;
  clearRetryTimer: () => void;
  scheduleRetry: (reason: string) => void;
  retryAttemptRef: React.MutableRefObject<number>;
  userInitiatedDisconnectRef: React.MutableRefObject<boolean>;
  retryHaltedRef: React.MutableRefObject<boolean>;
}


// ============================================================================
// Hook
// ============================================================================

export function useConnectionManager({
  config,
  send,
  websocketServiceRef,
  audioManagerRef,
  systemInstructionsRef,
  pendingSessionConfigRef,
  isRecordingForBlobRef,
  recordedAudioChunksRef,
}: UseConnectionManagerParams): UseConnectionManagerReturn {
  
  // Retry/backoff controls
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userInitiatedDisconnectRef = useRef(false);
  const retryHaltedRef = useRef(false);
  const lastConnectOptionsRef = useRef<ConnectOptions | undefined>(undefined);

  // Clear any pending retry timer
  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Schedule a retry with exponential backoff
  const scheduleRetry = useCallback((reason: string) => {
    if (!config.retryOnError) return;
    if (retryHaltedRef.current) return;
    if (retryTimerRef.current) return; // don't stack retries

    const maxAttempts = config.maxRetryAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS;
    const baseDelay = config.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;

    retryAttemptRef.current += 1;
    const attempt = retryAttemptRef.current;

    if (attempt > maxAttempts) {
      const err: any = new Error('AI_SERVICE_UNAVAILABLE');
      err.code = 'AI_SERVICE_UNAVAILABLE';
      err.source = 'api.openai.com';
      retryHaltedRef.current = true;
      send({ type: 'CONNECTION_ERROR', error: err });
      return;
    }

    const exp = Math.min(MAX_RETRY_DELAY_MS, baseDelay * Math.pow(2, attempt - 1));
    const jitter = Math.floor(Math.random() * RETRY_JITTER_MAX_MS);
    const delay = exp + jitter;

    console.warn(`[WebSocket] 🔁 Retry scheduled #${attempt}/${maxAttempts} in ${delay}ms (${reason})`);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      send({ type: 'RETRY' });
      connect(lastConnectOptionsRef.current);
    }, delay);
  }, [config.retryOnError, config.maxRetryAttempts, config.retryBaseDelayMs, send]);

  // Connect to OpenAI Realtime API via WebSockets
  const connect = useCallback(async (options?: ConnectOptions) => {
    const connectOptions = options ?? lastConnectOptionsRef.current ?? {};
    lastConnectOptionsRef.current = connectOptions;
    const allowKeyboardInput = connectOptions.allowKeyboardInput !== false;
    const initialInputMode = connectOptions.initialInputMode ?? 'ptt';
    const shouldInitMicrophone = !allowKeyboardInput || initialInputMode === 'voice' || initialInputMode === 'ptt';
    const enableVAD = shouldInitMicrophone ? config.enableVAD !== false : false;
    try {
      userInitiatedDisconnectRef.current = false;
      retryHaltedRef.current = false;
      clearRetryTimer();
      
      // Dispatch CONNECT event to state machine
      send({ type: 'CONNECT' });

      const audioManager = audioManagerRef.current;
      const websocketService = websocketServiceRef.current;

      if (!audioManager || !websocketService) {
        throw new Error('Services not initialized');
      }

      // Initialize audio if not already done
      if (!audioManager.getAudioContext()) {
        console.error('[🎙️ AEC] ❌ AudioContext should have been initialized early!');
        await audioManager.initializeAudio();
      }
      
      // 1. Fetch ephemeral token from backend
      logger.info('[WebSocket] Fetching ephemeral token from backend...');
      
      // apiBaseUrl is REQUIRED - must be provided by the consuming application
      if (!config.apiBaseUrl) {
        throw new Error('apiBaseUrl is required in WebSocketSessionConfig');
      }
      const apiBaseUrl = config.apiBaseUrl;
      
      // Use voice from pending session config if available
      // 🚨 GA API: voice goes into audio.output.voice (handled inside WebSocketService.connect)
      // temperature is no longer a valid field in the GA API
      const voice = pendingSessionConfigRef.current?.voice || config.voice;
      logger.debug('[WebSocket] Using voice:', voice, '(from', pendingSessionConfigRef.current?.voice ? 'scenario' : 'config', ')');
      
      // Build request body
      const requestBody: { model?: string; voice?: string } = {};
      if (config.model) {
        requestBody.model = config.model;
      }
      if (voice && typeof voice === 'string' && voice.trim() !== '') {
        requestBody.voice = voice;
      }
      
      // Use provided header creator or default
      const createHeaders = config.createAuthHeaders ?? defaultCreateHeaders;
      
      const tokenResponse = await fetch(`${apiBaseUrl}/realtime-ws/token`, {
        method: 'POST',
        headers: createHeaders(config.credentials ?? null),
        body: JSON.stringify(requestBody),
      });

      if (!tokenResponse.ok) {
        let errorMessage = `Failed to fetch token: ${tokenResponse.statusText}`;
        let errorSource: string | undefined = undefined;
        try {
          const errorData = await tokenResponse.json();
          if (errorData.message) {
            errorMessage = `Failed to fetch token: ${errorData.message}`;
          }
          if (errorData.receivedVoice) {
            errorMessage += ` (received voice: "${errorData.receivedVoice}")`;
          }
          if (errorData.source) {
            errorSource = String(errorData.source);
          }
          console.error('[WebSocket] Token fetch error details:', errorData);
        } catch (e) {
          // If JSON parsing fails, use status text
        }
        const err: any = new Error(errorMessage);
        err.statusCode = tokenResponse.status;
        err.source = errorSource || apiBaseUrl;
        if (tokenResponse.status === 429) {
          retryHaltedRef.current = true;
        }
        throw err;
      }

      const { token: ephemeralToken, expiresAt } = await tokenResponse.json();
      logger.info('[WebSocket] Got ephemeral token (expires:', new Date(expiresAt * 1000).toISOString(), ')');

      // 2. Get system instructions
      const instructions = systemInstructionsRef.current || config.systemInstructions;
      
      if (!instructions || instructions.trim() === '') {
        throw new Error('🚨 CRITICAL: System instructions are EMPTY at connect time! This WILL cause role-switching. Fix the code that calls connect().');
      }
      
      logger.debug('[WebSocket] Instructions available at connect time. Length:', instructions.length);

      // 3. Setup recording (microphone) only when required
      try {
        if (shouldInitMicrophone) {
          await audioManager.ensureMicrophoneReady();

          const micStream = audioManager.getMediaStream();
          if (!micStream) {
            throw new Error('Failed to get microphone stream from audio manager');
          }

          logger.info('[🎙️ AEC] ✅ Microphone setup complete');
        }

        // 4. Connect to OpenAI WebSocket
        const finalInstructions = instructions;

        await websocketService.connect(ephemeralToken, finalInstructions, {
          voice: voice,
          model: config.model,
          enableVAD: enableVAD,
          vadThreshold: config.vadThreshold || 0.5,
          // GA API: temperature is fixed by the server, no per-session override.
          // Audio format is sent via session.update (audio.input.format / audio.output.format) — see WebSocketService.sendSessionUpdate.
        });

        logger.info('[WebSocket] ✅ Connected successfully');
        retryAttemptRef.current = 0;
        clearRetryTimer();

        // 4b. Apply pending session config (tools, turn_detection, etc.)
        // connect() only sends instructions - tools (e.g. end_conversation for demo) must be sent separately
        const pending = pendingSessionConfigRef.current;
        if (pending && (pending.tools || pending.tool_choice)) {
          logger.debug('[WebSocket] Applying pending session config (tools)');
          await websocketService.updateSessionConfig(pending);
        }

        // Dispatch CONNECTED event
        send({ type: 'CONNECTED' });

        // 5. Connect audio data callback
        audioManager.on('onAudioData', (pcm16Data: Uint8Array) => {
          const audioData = pcm16Data;

          // Record for WAV blob if enabled
          if (isRecordingForBlobRef.current) {
            recordedAudioChunksRef.current.push(audioData);
          }

          // Send to WebSocket
          websocketService.sendAudio(audioData);
        });

        // Auto-start recording if configured and microphone is initialized
        if (shouldInitMicrophone) {
          if (config.autoStartRecording) {
            audioManager.startRecording();
          } else {
            audioManager.pauseMicrophone();
          }
        }
      } catch (micError) {
        if (!allowKeyboardInput) {
          throw micError;
        }

        // Microphone error - fallback to keyboard mode
        const errorMessage = (micError as Error).message || '';
        console.warn('[⌨️ FALLBACK] Microphone failed, switching to keyboard mode:', errorMessage);

        send({ type: 'SET_INPUT_MODE', mode: 'keyboard' });
        send({ type: 'SET_MICROPHONE_FALLBACK', enabled: true });

        if (!instructions || instructions.trim() === '') {
          throw new Error('🚨 CRITICAL: Cannot connect in keyboard mode without instructions!');
        }

        await websocketService.connect(ephemeralToken, instructions, {
          voice: config.voice,
          model: config.model,
          enableVAD: false,
          // GA API: temperature is fixed by the server, no per-session override.
          // Audio format is sent via session.update (audio.input.format / audio.output.format) — see WebSocketService.sendSessionUpdate.
        });

        // Apply pending session config (tools) in keyboard fallback too
        const pending = pendingSessionConfigRef.current;
        if (pending && (pending.tools || pending.tool_choice)) {
          logger.debug('[WebSocket] Applying pending session config (tools)');
          await websocketService.updateSessionConfig(pending);
        }

        logger.info('[⌨️ FALLBACK] ✅ Connected in keyboard-only mode (mic unavailable)');
        send({ type: 'CONNECTED' });
      }

    } catch (error) {
      console.error('Connection failed:', error);
      send({ type: 'CONNECTION_ERROR', error: error as Error });

      if (config.retryOnError) {
        const statusCode = (error as any)?.statusCode;
        if (statusCode === 429) {
          retryHaltedRef.current = true;
          return;
        }
        scheduleRetry('connect_failed');
      }
    }
  }, [
    config.autoStartRecording, 
    config.retryOnError, 
    config.maxRetryAttempts, 
    config.retryBaseDelayMs, 
    config.model,
    config.apiBaseUrl,
    config.voice,
    config.credentials,
    config.createAuthHeaders,
    config.systemInstructions,
    config.enableVAD,
    config.vadThreshold,
    clearRetryTimer,
    scheduleRetry,
    send,
    websocketServiceRef,
    audioManagerRef,
    systemInstructionsRef,
    pendingSessionConfigRef,
    isRecordingForBlobRef,
    recordedAudioChunksRef,
  ]);

  // Disconnect from OpenAI Realtime API
  const disconnect = useCallback(async () => {
    try {
      const audioManager = audioManagerRef.current;
      const websocketService = websocketServiceRef.current;

      clearRetryTimer();
      retryAttemptRef.current = 0;
      userInitiatedDisconnectRef.current = true;
      retryHaltedRef.current = true;
      audioManager?.stopRecording();
      websocketService?.disconnect();
      
      // Dispatch DISCONNECT event
      send({ type: 'DISCONNECT' });
      send({ type: 'RECORDING_STOPPED' });
      send({ type: 'PLAYBACK_STOPPED' });

    } catch (error) {
      console.error('Disconnect failed:', error);
      send({ type: 'CONNECTION_ERROR', error: error as Error });
    }
  }, [clearRetryTimer, send, audioManagerRef, websocketServiceRef]);

  return {
    connect,
    disconnect,
    clearRetryTimer,
    scheduleRetry,
    retryAttemptRef,
    userInitiatedDisconnectRef,
    retryHaltedRef,
  };
}
